import React, { useState, useEffect, useCallback } from 'react';
import {
  UserPlusIcon,
  EnvelopeIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';
import { toast } from '../utils/toast';
import { roleService, Role, UserWithRoles, Invitation } from '../services/role.service';
import { userService } from '../services/user.service';
import { UserEditModal, UserEditData } from './UserEditModal';
import { UserListItem, UserData, UserListPagination } from './users';

interface RoleManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: UserWithRoles;
  availableRoles: Role[];
  currentUserId?: string;
}

/**
 * The role an org user has: users.role, which their access is built from. The role assignments
 * are only a fallback for rows that do not carry it.
 */
const currentRoleName = (user: UserWithRoles): string | undefined =>
  user.role || user.primaryRole?.name || user.roles?.[0]?.name;

/**
 * Change a user's role. A user has one role, and it is saved on the user (PUT /auth/users/:id),
 * which is what their access is built from; the server keeps the role assignments in line.
 */
const RoleManagementModal: React.FC<RoleManagementModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  user,
  availableRoles,
  currentUserId
}) => {
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const isSelf = user.id === currentUserId;
  const currentRole = currentRoleName(user);

  // Start from the user's current role when the modal opens or the user changes
  useEffect(() => {
    setSelectedRole(currentRole || '');
  }, [currentRole]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRole) {
      toast.error('Choose a role');
      return;
    }
    if (selectedRole === currentRole) {
      onClose();
      return;
    }

    setLoading(true);
    try {
      await roleService.updateUser(user.id, { role: selectedRole });
      const roleLabel = availableRoles.find(r => r.name === selectedRole)?.displayName || selectedRole;
      toast.success(`${user.firstName} ${user.lastName} is now ${roleLabel}.`);

      // Call onSuccess to refresh the parent component's data
      await onSuccess();

      // Close the modal
      onClose();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update user role');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <ShieldCheckIcon className="h-5 w-5 mr-2" />
            Manage User Roles
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600">
            {user.firstName} {user.lastName} ({user.email})
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Current role: {availableRoles.find(r => r.name === currentRole)?.displayName || currentRole || 'None'}
          </p>
          {isSelf && (
            <p className="text-xs text-amber-700 mt-2">You cannot change your own role.</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <span id="manage-role-label" className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </span>
            {availableRoles.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                <span className="ml-2 text-sm text-gray-500">Loading roles...</span>
              </div>
            ) : (
              <div role="radiogroup" aria-labelledby="manage-role-label" className="space-y-2 max-h-60 overflow-y-auto">
                {availableRoles.map(role => (
                  <label key={role.id} className="flex items-center p-2 border rounded hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="manage-role"
                      value={role.name}
                      checked={selectedRole === role.name}
                      onChange={() => setSelectedRole(role.name)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                      disabled={loading || isSelf}
                    />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">
                        {role.displayName}
                      </span>
                      <span
                        className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                        style={{ backgroundColor: role.color + '20', color: role.color }}
                      >
                        Level {role.level}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">{role.description}</p>
                    <p className="text-xs text-gray-400">
                      {role.permissions.length} permissions
                    </p>
                  </div>
                </label>
              ))}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || isSelf || !selectedRole || selectedRole === currentRole}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Updating...' : 'Update Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  availableRoles: Role[];
  departments: string[];
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  availableRoles,
  departments
}) => {
  const [email, setEmail] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [department, setDepartment] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // A user has one role; the invitation carries it.
    const role = availableRoles.find(r => r.id === selectedRoleId);
    if (!role) {
      toast.error('Please choose a role');
      return;
    }

    setLoading(true);
    try {
      await roleService.inviteUser({
        email,
        role: role.name as any,
        metadata: {
          firstName,
          lastName,
          department
        }
      });

      toast.success(`Invitation sent to ${email}`);
      onSuccess();
      onClose();

      // Reset form
      setEmail('');
      setSelectedRoleId('');
      setFirstName('');
      setLastName('');
      setDepartment('');
    } catch (error: any) {
      toast.error(error.message || 'Failed to invite user');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Invite New User</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Department (Optional)</label>
            <select
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Select Department</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          <div>
            <span id="invite-role-label" className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </span>
            <div role="radiogroup" aria-labelledby="invite-role-label" className="space-y-2 max-h-48 overflow-y-auto border rounded p-2">
              {availableRoles.map(role => (
                <label key={role.id} className="flex items-center">
                  <input
                    type="radio"
                    name="invite-role"
                    value={role.name}
                    checked={selectedRoleId === role.id}
                    onChange={() => setSelectedRoleId(role.id)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-900">{role.displayName}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedRoleId}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const UserManagementRBAC: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<UserWithRoles | null>(null);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<UserEditData | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: '',
    roleId: '',
    isActive: undefined as boolean | undefined
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20
  });

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const fetchRolesAndDepartments = useCallback(async () => {
    try {
      const [rolesData, depts] = await Promise.all([
        roleService.getRoles(),
        userService.getDepartments()
      ]);
      // Filter out superadmin role - org admins can only assign admin, operator, staff
      const orgRoles = rolesData.filter(role =>
        ['admin', 'operator', 'staff'].includes(role.name)
      );
      setAvailableRoles(orgRoles);
      setDepartments(depts);
    } catch (error) {
      console.error('Failed to fetch roles and departments:', error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersData, invitationsData] = await Promise.all([
        roleService.getUsersWithRoles(filters),
        roleService.getInvitations()
      ]);

      setUsers(usersData.users);
      setInvitations(invitationsData);
      setPagination({
        total: usersData.total,
        page: usersData.page,
        limit: usersData.limit
      });

      console.log('Fetched users data:', usersData.users.length, 'users');
      console.log('Users with roles:', usersData.users.map(u => ({
        id: u.id,
        name: `${u.firstName} ${u.lastName}`,
        roles: u.roles.map(r => r.displayName)
      })));
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast.error('Failed to fetch user data');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchRolesAndDepartments();
  }, [fetchRolesAndDepartments]);

  const handleSearch = (searchTerm: string) => {
    setFilters(prev => ({ ...prev, search: searchTerm, page: 1 }));
  };

  const handleFilterChange = (key: keyof typeof filters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleLimitChange = (limit: number) => {
    setFilters(prev => ({ ...prev, limit, page: 1 }));
    setPagination(prev => ({ ...prev, limit }));
  };

  const getRoleColor = (role: Role) => {
    return role.color || '#6B7280';
  };

  const getStatusColor = (isActive: boolean, status?: string) => {
    if (status === 'pending') return 'bg-yellow-100 text-yellow-800';
    return isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const getStatusIcon = (isActive: boolean, status?: string) => {
    if (status === 'pending') return <EnvelopeIcon className="h-4 w-4" />;
    return isActive ? <CheckCircleIcon className="h-4 w-4" /> : <XCircleIcon className="h-4 w-4" />;
  };

  const handleResendInvitation = async (invitation: Invitation) => {
    try {
      await roleService.resendInvitation(invitation.id);
      toast.success(`Invitation resent to ${invitation.email}`);
      await fetchData(); // Refresh data
    } catch (error: any) {
      toast.error(error.message || 'Failed to resend invitation');
    }
  };

  const handleCancelInvitation = async (invitation: Invitation) => {
    try {
      await roleService.cancelInvitation(invitation.id);
      toast.success(`Invitation cancelled for ${invitation.email}`);
      await fetchData(); // Refresh data
    } catch (error: any) {
      toast.error(error.message || 'Failed to cancel invitation');
    }
  };

  const handleEditUser = (userData: UserWithRoles) => {
    // Convert UserWithRoles to UserEditData format
    const editData: UserEditData = {
      id: userData.id,
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      role: (currentRoleName(userData) || 'staff') as 'admin' | 'operator' | 'staff',
      isActive: userData.isActive,
      profile: userData.profile
    };
    setSelectedUserForEdit(editData);
    setShowEditModal(true);
  };

  const handleSaveUser = async (userId: string, data: Partial<UserEditData>) => {
    try {
      // The profile edit has no role field; roles change only through Manage roles, so a stale
      // role here can never overwrite the user's real one.
      await roleService.updateUser(userId, { ...data, role: undefined });
      toast.success('User updated successfully');
      await fetchData(); // Refresh data
      setShowEditModal(false);
      setSelectedUserForEdit(null);
    } catch (error: any) {
      toast.error(error.message || 'Failed to update user');
      throw error; // Re-throw so the modal can handle the error state
    }
  };

  const handleSendPasswordReset = async (userId: string) => {
    try {
      const result = await roleService.sendPasswordReset(userId);
      toast.success(result.message || 'Password reset email sent');
    } catch (error: any) {
      toast.error(error.message || 'Failed to send password reset email');
      throw error;
    }
  };

  const isInvitationExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  const getInvitationStatusColor = (invitation: Invitation) => {
    if (invitation.status === 'expired' || isInvitationExpired(invitation.expiresAt)) {
      return 'bg-red-100 text-red-800';
    }
    switch (invitation.status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTimeRemaining = (expiresAt: string) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffMs = expiry.getTime() - now.getTime();

    if (diffMs <= 0) return 'Expired';

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}m remaining`;
    } else {
      return `${diffMinutes}m remaining`;
    }
  };

  const getStatusText = (isActive: boolean, status?: string) => {
    if (status === 'pending') return 'Pending';
    return isActive ? 'Active' : 'Inactive';
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-8">
        <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Access Restricted</h3>
        <p className="text-gray-500">Only administrators can manage users.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">User Management</h2>
          <p className="text-sm text-gray-500">
            Manage users and their role assignments in your organization
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowInviteModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <UserPlusIcon className="h-4 w-4 mr-2" />
            Invite User
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <div className="relative">
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search users..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              value={filters.roleId}
              onChange={(e) => handleFilterChange('roleId', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Roles</option>
              {availableRoles.map(role => (
                <option key={role.id} value={role.id}>{role.displayName}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filters.isActive === undefined ? 'all' : filters.isActive.toString()}
              onChange={(e) => handleFilterChange('isActive',
                e.target.value === 'all' ? undefined : e.target.value === 'true'
              )}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* User List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <p className="mt-2 text-gray-500">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center">
            <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
            <p className="text-gray-500 mb-4">
              {filters.search ? 'No users match your search criteria.' : 'Get started by inviting your first user.'}
            </p>
            <button
              onClick={() => setShowInviteModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <UserPlusIcon className="h-4 w-4 mr-2" />
              Invite User
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {users.map((userData) => (
              <UserListItem
                key={userData.id}
                user={userData as UserData}
                variant="list-item"
                showDepartment={true}
                showStatus={true}
                renderActions={(user) => (
                  <>
                    <button
                      onClick={() => handleEditUser(userData)}
                      className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                      title="Edit user"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedUser(userData);
                        setShowRoleModal(true);
                      }}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Manage roles"
                    >
                      <Cog6ToothIcon className="h-4 w-4" />
                    </button>
                  </>
                )}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      <UserListPagination
        pagination={pagination}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        loading={loading}
      />

      {/* Pending Invitations Section */}
      {invitations.length > 0 && (
        <div className="mt-8">
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <EnvelopeIcon className="h-5 w-5 mr-2 text-gray-500" />
                Pending Invitations ({invitations.filter(i => i.status === 'pending').length})
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Manage sent invitations and resend expired ones
              </p>
            </div>

            <div className="divide-y divide-gray-200">
              {invitations.map((invitation) => {
                const isExpired = invitation.status === 'expired' || isInvitationExpired(invitation.expiresAt);
                const timeRemaining = getTimeRemaining(invitation.expiresAt);

                return (
                  <div key={invitation.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                              <EnvelopeIcon className="h-4 w-4 text-gray-500" />
                            </div>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {invitation.email}
                              </p>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getInvitationStatusColor(invitation)}`}>
                                {invitation.status}
                              </span>
                            </div>

                            <div className="flex items-center space-x-4 mt-1">
                              <p className="text-sm text-gray-500">
                                Role: <span className="font-medium capitalize">{invitation.role}</span>
                              </p>

                              {invitation.status === 'pending' && (
                                <p className="text-sm text-gray-500">
                                  {isExpired ? (
                                    <span className="text-red-600 font-medium">Expired</span>
                                  ) : (
                                    timeRemaining
                                  )}
                                </p>
                              )}

                              {invitation.emailSent === false && invitation.emailError && (
                                <p className="text-sm text-red-600">
                                  ⚠️ Email failed to send
                                </p>
                              )}
                            </div>

                            {invitation.metadata?.firstName && invitation.metadata?.lastName && (
                              <p className="text-sm text-gray-500 mt-1">
                                Name: {invitation.metadata.firstName} {invitation.metadata.lastName}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        {invitation.status === 'pending' && (
                          <>
                            {isExpired ? (
                              <button
                                onClick={() => handleResendInvitation(invitation)}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                              >
                                <ArrowPathIcon className="h-3 w-3 mr-1" />
                                Resend
                              </button>
                            ) : (
                              <button
                                onClick={() => handleResendInvitation(invitation)}
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                              >
                                <ArrowPathIcon className="h-3 w-3 mr-1" />
                                Resend
                              </button>
                            )}

                            <button
                              onClick={() => handleCancelInvitation(invitation)}
                              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                              <XMarkIcon className="h-3 w-3 mr-1" />
                              Cancel
                            </button>
                          </>
                        )}

                        {invitation.status === 'accepted' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircleIcon className="h-3 w-3 mr-1" />
                            Accepted
                          </span>
                        )}

                        {invitation.status === 'cancelled' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            <XCircleIcon className="h-3 w-3 mr-1" />
                            Cancelled
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {invitations.filter(i => i.status === 'pending').length === 0 && (
              <div className="px-6 py-8 text-center">
                <EnvelopeIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No pending invitations</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={fetchData}
        availableRoles={availableRoles}
        departments={departments}
      />

      {showRoleModal && selectedUser && (
        <RoleManagementModal
          isOpen={showRoleModal}
          onClose={() => {
            setShowRoleModal(false);
            setSelectedUser(null);
          }}
          onSuccess={fetchData}
          user={selectedUser}
          availableRoles={availableRoles}
          currentUserId={user?.id}
        />
      )}

      <UserEditModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedUserForEdit(null);
        }}
        onSave={handleSaveUser}
        onSendPasswordReset={handleSendPasswordReset}
        user={selectedUserForEdit}
        departments={departments}
        currentUserId={user?.id}
      />
    </div>
  );
};